import React, { useCallback, useContext } from 'react';
import useSafeState from '@ubeya/shared/hooks/useSafeState';
import DropDownContext from '../../contexts/DropDownContext';
import ConfirmationModal from '../ConfirmationModal';

const PromptClick = ({
  onClick,
  children,
  title = 'areYouSure',
  body = 'areYouSure',
  renderBody,
  confirmText,
  disabledConfirm,
  showCancelButton = true,
  usePortal,
  stopPropagation = false,
  cancelText = 'cancel',
  disabledConfirmTitle = false,
  enable = true,
  isOpen: defaultIsOpen = false,
  onClose,
  isWaitingForResponse = true,
  errors = [],
  confirmationModalContainerStyle,
  confirmationModalWrapperStyle,
  dataTestId,
  warnings = [],
  isPermanentAction = false
}) => {
  const { closeMenu } = useContext(DropDownContext);
  const [isOpen, setIsOpen] = useSafeState(defaultIsOpen);
  const [isLoading, setIsLoading] = useSafeState(false);

  const handleClose = useCallback(() => {
    if (defaultIsOpen) {
      onClose();
    } else {
      setIsOpen(false);
      closeMenu?.();
      onClose?.();
    }
  }, [closeMenu, defaultIsOpen, onClose, setIsOpen]);

  if (!enable) {
    return React.cloneElement(React.Children.only(children), {
      onClick: (e) => {
        if (stopPropagation) {
          e.stopPropagation();
          e.preventDefault();
        }
        onClick?.(e);
      }
    });
  }

  if (!isOpen) {
    return React.cloneElement(React.Children.only(children), {
      onClick: (e) => {
        if (stopPropagation) {
          e.stopPropagation();
          e.preventDefault();
        }
        setIsOpen(true);
      }
    });
  }

  return (
    <>
      {children}
      <ConfirmationModal
        confirmationModalContainerStyle={confirmationModalContainerStyle}
        confirmationModalWrapperStyle={confirmationModalWrapperStyle}
        onClose={handleClose}
        title={!disabledConfirmTitle ? title : ''}
        body={renderBody ? undefined : body}
        dataTestId={dataTestId}
        onConfirm={async (e) => {
          try {
            if (isWaitingForResponse) {
              setIsLoading(true);
              await onClick(e);
              setIsLoading(false);
            } else {
              onClick(e);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error);
          } finally {
            handleClose();
          }
        }}
        confirmText={confirmText}
        cancelText={cancelText}
        loadingConfirm={isLoading}
        warnings={warnings}
        disabledConfirm={disabledConfirm}
        errors={errors}
        showCancelButton={showCancelButton}
        usePortal={usePortal}
        isPermanentAction={isPermanentAction}>
        {renderBody}
      </ConfirmationModal>
    </>
  );
};

export default PromptClick;
